import {
  CuripodDecendant,
  CuripodElement,
  isCuripodMathBlockElement,
  isCuripodMathInlineElement,
  parseStringToSlateObject,
} from '../components/SlideComponents/slateUtil';

export interface WordOffset {
  word: string;
  /** Start character index in the plain text */
  startChar: number;
  /** End character index (exclusive) in the plain text */
  endChar: number;
  /** Slate element index (top-level paragraph) */
  elementIdx: number;
  /** Slate child index within the element */
  childIdx: number;
  /** Character offset within the Slate text leaf where this word starts */
  slateCharStart: number;
  /** Character offset within the Slate text leaf where this word ends (exclusive) */
  slateCharEnd: number;
}

export interface WordTiming {
  word: string;
  startTime: number;
  endTime: number;
  elementIdx: number;
  childIdx: number;
  slateCharStart: number;
  slateCharEnd: number;
}

export interface ElevenLabsAlignment {
  characters: string[];
  character_start_times_seconds: number[];
  character_end_times_seconds: number[];
}

const hasStringText = (node: CuripodDecendant): node is { text: string } & object =>
  typeof (node as { text?: unknown }).text === 'string';

/**
 * Best-effort conversion of a LaTeX math value into something ElevenLabs can voice.
 * Returns null when the input still contains unrecognised LaTeX after substitution
 * (backslash commands, residual ^ / _ / braces) — the caller treats null as "skip",
 * so unintelligible expressions stay silent rather than reading out raw LaTeX.
 *
 * Dollar signs are always stripped from the output, so the TTS endpoint never
 * receives `$...$` wrappers.
 */
const mathToSpeech = (value: string): string | null => {
  let s = value;

  // Multi-arg structural commands first so their inner contents are exposed
  // to the symbol pass below. Iterate to handle simple nesting.
  for (let i = 0; i < 5; i++) {
    const before = s;
    s = s.replace(/\\frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, ' $1 over $2 ');
    s = s.replace(/\\sqrt\s*\{([^{}]+)\}/g, ' square root of $1 ');
    if (s === before) break;
  }

  // Powers — specific cases before the generic fallback. Trailing spaces in
  // the replacements compensate for the trailing `\s*` in each pattern, which
  // would otherwise swallow the separator before the next token.
  s = s.replace(/\^\s*\{?\s*\\circ\s*\}?/g, ' degrees ');
  s = s.replace(/\^\s*\{?\s*2\s*\}?/g, ' squared ');
  s = s.replace(/\^\s*\{?\s*3\s*\}?/g, ' cubed ');
  s = s.replace(/\^\s*\{([^{}]+)\}/g, ' to the $1 ');
  s = s.replace(/\^([A-Za-z0-9])/g, ' to the $1 ');

  // Subscripts after a letter ("H_2O" → "H sub 2 O").
  s = s.replace(/([A-Za-z])_\s*\{([^{}]+)\}/g, '$1 sub $2 ');
  s = s.replace(/([A-Za-z])_([A-Za-z0-9])/g, '$1 sub $2 ');

  // Common symbols and operators.
  s = s.replace(/\\degrees?\b/g, ' degrees');
  s = s.replace(/\\textdegree\b/g, ' degrees');
  s = s.replace(/\\pi\b/g, ' pi ');
  s = s.replace(/\\times\b/g, ' times ');
  s = s.replace(/\\cdot\b/g, ' times ');
  s = s.replace(/\\div\b/g, ' divided by ');
  s = s.replace(/\\pm\b/g, ' plus or minus ');
  s = s.replace(/\\leq?\b/g, ' less than or equal to ');
  s = s.replace(/\\geq?\b/g, ' greater than or equal to ');
  s = s.replace(/\\neq?\b/g, ' not equal to ');
  s = s.replace(/\\approx\b/g, ' approximately ');
  s = s.replace(/\\infty\b/g, ' infinity ');

  // Whitespace macros and tildes.
  s = s.replace(/\\[,;:!]/g, ' ');
  s = s.replace(/~/g, ' ');

  // Strip leftover decorative braces and stray dollar signs — we never want
  // either reaching the TTS endpoint.
  s = s.replace(/[{}$]/g, '');

  // Temperature post-pass — only fires when a preceding "degrees" was produced
  // by the ^\circ conversion, so standalone C/F variables (e.g. "F = ma") are
  // left alone.
  s = s.replace(/\bdegrees\s+C\b/g, ' degrees Celsius ');
  s = s.replace(/\bdegrees\s+F\b/g, ' degrees Fahrenheit ');

  // Normalise whitespace.
  s = s.replace(/\s+/g, ' ').trim();

  // Any remaining LaTeX residue means we couldn't safely voice it.
  if (s.length === 0) return null;
  if (/[\\^_]/.test(s)) return null;

  return s;
};

/**
 * Render a Slate child as the plain-text fragment it should contribute to the TTS
 * payload. Math elements are best-effort converted to spoken English via
 * mathToSpeech; expressions we can't translate return null and are skipped, so
 * audio is silent across that span rather than reading raw LaTeX or dollar signs.
 */
const getRenderableText = (node: CuripodDecendant): string | null => {
  if (hasStringText(node)) return node.text;
  if (isCuripodMathInlineElement(node)) return mathToSpeech(node.value);
  if (isCuripodMathBlockElement(node)) return mathToSpeech(node.value);
  return null;
};

/**
 * Extract plain text from Slate JSON content, tracking each word's position
 * back to its Slate path for highlighting.
 */
export function extractPlainTextWithOffsets(
  slateContent: string,
): {
  plainText: string;
  wordMap: WordOffset[];
} {
  const elements = parseStringToSlateObject(slateContent);
  let plainText = '';
  const wordMap: WordOffset[] = [];
  // Tracks whether we've emitted anything yet so we can insert the
  // inter-element space lazily, only when a later element actually contributes.
  let hasEmittedAny = false;

  const pushWords = (
    text: string,
    elementIdx: number,
    childIdx: number,
    baseOffset: number,
  ) => {
    const wordRegex = /\S+/g;
    let match: RegExpExecArray | null;
    while ((match = wordRegex.exec(text)) !== null) {
      wordMap.push({
        word: match[0],
        startChar: baseOffset + match.index,
        endChar: baseOffset + match.index + match[0].length,
        elementIdx,
        childIdx,
        slateCharStart: match.index,
        slateCharEnd: match.index + match[0].length,
      });
    }
  };

  for (let elementIdx = 0; elementIdx < elements.length; elementIdx++) {
    const element = elements[elementIdx];

    if (isCuripodMathBlockElement(element)) {
      const text = mathToSpeech(element.value);
      if (!text) continue;
      if (hasEmittedAny) plainText += ' ';
      // Use childIdx -1 (sentinel) so decorate() never matches the math-block's
      // empty text leaf at [elementIdx, 0] — an in-bounds offset check there
      // would exceed the leaf length and crash Slate.
      pushWords(text, elementIdx, -1, plainText.length);
      plainText += text;
      hasEmittedAny = true;
      continue;
    }

    const children = getSpeakableChildren(element);
    let elementHasEmitted = false;

    for (const { node, childIdx } of children) {
      const text = getRenderableText(node);
      if (!text) continue;

      // Insert the inter-element separator lazily — once per element, before
      // its first contributing child.
      if (!elementHasEmitted && hasEmittedAny) plainText += ' ';

      // Push words for all voiced nodes. For inline math, childIdx is the real
      // Slate index of the math-inline element — decorate()'s `'text' in node`
      // guard means it will never match the element node itself, so no crash.
      pushWords(text, elementIdx, childIdx, plainText.length);

      plainText += text;
      elementHasEmitted = true;
    }

    if (elementHasEmitted) hasEmittedAny = true;
  }

  return { plainText, wordMap };
}

/**
 * Get the flat list of speakable children from a Slate element paired with their
 * real Slate child index, handling list-item nesting. Returns text leaves and
 * math-inline nodes; everything else (images, prompt placeholders, etc.) is
 * skipped.
 *
 * Preserving the real Slate index is critical: the downstream decorate() function
 * in ReadAloudHighlightedTextViewer matches `path[1] === word.childIdx`, so a
 * filtered-array index would silently break highlighting for any text that follows
 * a skipped element.
 */
function getSpeakableChildren(
  element: CuripodElement,
): { node: CuripodDecendant; childIdx: number }[] {
  if (!element.children) return [];

  const result: { node: CuripodDecendant; childIdx: number }[] = [];
  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    if (hasStringText(child)) {
      result.push({ node: child, childIdx: i });
    } else if (isCuripodMathInlineElement(child)) {
      result.push({ node: child, childIdx: i });
    } else if (
      (child as CuripodElement).type === 'list-item' &&
      Array.isArray((child as CuripodElement).children)
    ) {
      // Unwrap list items — grandchildren share the list-item's index since
      // they live at depth 3 and decorate() only checks path.length === 2.
      for (const grandchild of (child as CuripodElement).children) {
        if (hasStringText(grandchild)) {
          result.push({ node: grandchild, childIdx: i });
        } else if (isCuripodMathInlineElement(grandchild)) {
          result.push({ node: grandchild, childIdx: i });
        }
      }
    }
    // Skip everything else (images, prompt elements, unknown types).
  }
  return result;
}

/**
 * Map ElevenLabs character-level alignment data onto our word offsets
 * to produce word-level timings.
 */
export function buildWordTimings(
  alignment: ElevenLabsAlignment,
  wordMap: WordOffset[],
): WordTiming[] {
  if (!alignment || wordMap.length === 0) return [];

  const timings: WordTiming[] = [];

  for (const word of wordMap) {
    // Find the character timing for the start of this word
    const startCharIdx = word.startChar;
    const endCharIdx = word.endChar - 1; // Last character of the word

    // Clamp to alignment array bounds
    const safeStart = Math.min(startCharIdx, alignment.characters.length - 1);
    const safeEnd = Math.min(endCharIdx, alignment.characters.length - 1);

    if (safeStart < 0 || safeEnd < 0) continue;

    timings.push({
      word: word.word,
      startTime: alignment.character_start_times_seconds[safeStart] ?? 0,
      endTime: alignment.character_end_times_seconds[safeEnd] ?? 0,
      elementIdx: word.elementIdx,
      childIdx: word.childIdx,
      slateCharStart: word.slateCharStart,
      slateCharEnd: word.slateCharEnd,
    });
  }

  return timings;
}
